import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window;
}

// Web Push requires the VAPID key as a Uint8Array, but browsers hand it to
// us as a URL-safe base64 string — this is the standard conversion.
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * Tracks whether push is supported/enabled on this device and exposes
 * subscribe/unsubscribe actions. Each browser/device gets its own
 * push_subscriptions row (see 20240339_push_notifications.sql), so this
 * reflects only the current device's state, not the user's account-wide state.
 */
export function usePushNotifications() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported] = useState(isPushSupported());

  useEffect(() => {
    if (!isSupported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setIsSubscribed(!!sub))
      .catch(() => setIsSubscribed(false));
  }, [isSupported]);

  const subscribe = useMutation({
    mutationFn: async () => {
      if (!isSupported) throw new Error("Push notifications aren't supported on this device.");
      if (!VAPID_PUBLIC_KEY) throw new Error("Push notifications aren't configured for this environment.");
      if (!user) throw new Error("Not signed in");

      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("Notification permission was not granted.");

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const keys = subscription.toJSON().keys as { p256dh: string; auth: string };
      const { error } = await supabase.from("push_subscriptions").upsert(
        {
          user_id: user.id,
          endpoint: subscription.endpoint,
          p256dh: keys.p256dh,
          auth: keys.auth,
          user_agent: navigator.userAgent,
        },
        { onConflict: "endpoint" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      setIsSubscribed(true);
      toast({ title: "Push notifications enabled", description: "You'll get alerts on this device." });
    },
    onError: (error: Error) => {
      toast({ title: "Couldn't enable push notifications", description: error.message, variant: "destructive" });
    },
  });

  const unsubscribe = useMutation({
    mutationFn: async () => {
      if (!isSupported) return;
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      await supabase.from("push_subscriptions").delete().eq("endpoint", subscription.endpoint);
      await subscription.unsubscribe();
    },
    onSuccess: () => {
      setIsSubscribed(false);
      toast({ title: "Push notifications disabled" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  return { isSupported, isSubscribed, subscribe, unsubscribe };
}
